import { useEffect, useState } from "react";
import { Bell, Plus, Trash2, History, ToggleLeft, ToggleRight } from "lucide-react";
import { alertsApi } from "../../api";
import { useAuth } from "../../hooks/useAuth";
import { isAdmin } from "../../constants/roles";
import { ChipInput, LoadingText, Select } from "../ui";

const OPERATORS = [
  { value: "gt",  label: "greater than (>)" },
  { value: "gte", label: "greater than or equal (≥)" },
  { value: "lt",  label: "less than (<)" },
  { value: "lte", label: "less than or equal (≤)" },
];

const OP_SYMBOLS: Record<string, string> = { gt: ">", gte: "≥", lt: "<", lte: "≤" };

interface AlertRule {
  id: string;
  name: string;
  description?: string | null;
  metric_sql: string;
  datasource_key?: string | null;
  operator: string;
  threshold: number;
  notify_emails?: string[];
  notify_slack?: boolean;
  notify_slack_channels?: string[];
  is_active?: boolean;
  cooldown_minutes?: number;
  last_fired_at?: string | null;
}

interface Firing {
  id: string;
  fired_at: string;
  message: string;
  triggered_value?: number | null;
}

interface AlertForm {
  name: string;
  description: string;
  metric_sql: string;
  datasource_key: string;
  operator: string;
  threshold: string | number;
  notify_emails: string[];
  notify_slack: boolean;
  notify_slack_channels: string[];
  is_active: boolean;
  cooldown_minutes: string | number;
}

const DEFAULT_FORM: AlertForm = {
  name: "",
  description: "",
  metric_sql: "",
  datasource_key: "",
  operator: "gt",
  threshold: "",
  notify_emails: [],
  notify_slack: false,
  notify_slack_channels: [],
  is_active: true,
  cooldown_minutes: 60,
};

function Badge({ fired }: { fired?: boolean }) {
  if (!fired) return null;
  return (
    <span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
      fired
    </span>
  );
}

function FiringHistory({ ruleId, onClose }: { ruleId: string; onClose: () => void }) {
  const [firings, setFirings] = useState<Firing[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    alertsApi.getAlertFirings(ruleId)
      .then((r) => setFirings(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [ruleId]);

  return (
    <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-600">Recent firings</span>
        <button onClick={onClose} className="text-xs text-gray-400 hover:text-gray-600">close</button>
      </div>
      {loading ? (
        <p className="text-xs text-gray-400">Loading...</p>
      ) : firings.length === 0 ? (
        <p className="text-xs text-gray-400">No firings recorded.</p>
      ) : (
        <ul className="space-y-1.5">
          {firings.map((f) => (
            <li key={f.id} className="text-xs text-gray-700">
              <span className="font-medium text-red-600">
                {new Date(f.fired_at).toLocaleString()}
              </span>
              {" — "}
              {f.message}
              {f.triggered_value != null && (
                <span className="ml-1 text-gray-500">(value: {Number(f.triggered_value).toLocaleString()})</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

type AlertPayload = Record<string, unknown>;

interface RuleFormProps {
  initial?: AlertForm;
  onSave: (payload: AlertPayload) => Promise<void> | void;
  onCancel: () => void;
}

function RuleForm({ initial, onSave, onCancel }: RuleFormProps) {
  const [form, setForm] = useState<AlertForm>(initial ?? DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof AlertForm>(field: K, value: AlertForm[K]) {
    setForm((f) => ({ ...f, [field]: value }) as AlertForm);
  }

  async function handleSave() {
    if (!form.name.trim()) { setError("Name is required"); return; }
    if (!form.metric_sql.trim()) { setError("SQL is required"); return; }
    if (form.threshold === "" || isNaN(Number(form.threshold))) { setError("Threshold must be a number"); return; }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      metric_sql: form.metric_sql.trim(),
      datasource_key: form.datasource_key.trim() || null,
      operator: form.operator,
      threshold: Number(form.threshold),
      notify_emails: form.notify_emails,
      notify_slack: form.notify_slack,
      notify_slack_channels: form.notify_slack ? form.notify_slack_channels : [],
      is_active: form.is_active,
      cooldown_minutes: Number(form.cooldown_minutes) || 60,
    };

    setSaving(true);
    setError(null);
    try {
      await onSave(payload);
    } catch { return; } finally {
      setSaving(false);
    }
  }

  const inputCls = "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500";

  return (
    <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">Name *</label>
          <input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="e.g. High error rate"
            className={inputCls}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">Datasource key</label>
          <input
            value={form.datasource_key}
            onChange={(e) => set("datasource_key", e.target.value)}
            placeholder="Leave blank for default"
            className={inputCls}
          />
        </div>
      </div>

      <div>
        <label className="mb-1 block text-xs font-semibold text-gray-700">Description</label>
        <input
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
          placeholder="Optional — shown in alert emails"
          className={inputCls}
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-semibold text-gray-700">
          Metric SQL *{" "}
          <span className="font-normal text-gray-400">must return a single numeric value</span>
        </label>
        <textarea
          value={form.metric_sql}
          onChange={(e) => set("metric_sql", e.target.value)}
          rows={4}
          placeholder={"SELECT COUNT(*) FROM errors WHERE error_time >= CURRENT_TIMESTAMP - INTERVAL '1 HOUR'"}
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 font-mono text-xs text-gray-900 placeholder-gray-400 leading-relaxed focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">Condition *</label>
          <Select
            value={form.operator}
            onChange={(e) => set("operator", e.target.value)}
            className="w-full"
          >
            {OPERATORS.map((op) => (
              <option key={op.value} value={op.value}>{op.label}</option>
            ))}
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">Threshold *</label>
          <input
            type="number"
            value={form.threshold}
            onChange={(e) => set("threshold", e.target.value)}
            placeholder="0"
            className={inputCls}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-700">Cooldown (minutes)</label>
          <input
            type="number"
            value={form.cooldown_minutes}
            onChange={(e) => set("cooldown_minutes", e.target.value)}
            min={1}
            className={inputCls}
          />
        </div>
      </div>

      <div className="border-t border-gray-200 pt-4">
        <label className="mb-2 block text-xs font-semibold text-gray-700">Where should it go?</label>
        <div className="space-y-2">
          <label className="flex cursor-pointer items-center gap-3 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={form.notify_slack}
              onChange={(e) => set("notify_slack", e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Send to Slack
          </label>
        </div>

        <div className="mt-3">
          <label className="mb-1 block text-xs font-medium text-gray-600">Email to (optional)</label>
          <ChipInput
            values={form.notify_emails}
            onChange={(next) => set("notify_emails", next)}
            placeholder="Add an email address and press Enter"
            validate={(v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)}
          />
          <p className="mt-1 text-xs text-gray-400">
            Anyone added here gets the alert by email — they don&apos;t need a Brian account.
          </p>
        </div>

        {form.notify_slack && (
          <div className="mt-3 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <label className="block text-xs font-medium text-gray-600">Slack channels</label>
            <ChipInput
              values={form.notify_slack_channels}
              onChange={(next) => set("notify_slack_channels", next)}
              placeholder="Add a channel ID (e.g. C0123456789) and press Enter"
            />
            <p className="text-xs text-gray-400">
              Brian must be invited to each channel. Leave empty to use the default alert channels. Find the channel ID in Slack under the channel name &rarr; About &rarr; Copy channel ID.
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center gap-6">
        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => set("is_active", e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Active
        </label>
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      <div className="flex gap-2 pt-1">
        <button
          onClick={handleSave}
          disabled={saving}
          className="rounded-lg bg-[#1D345F] px-4 py-2 text-sm font-medium text-white hover:bg-[#16294d] disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save rule"}
        </button>
        <button
          onClick={onCancel}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function CustomAlerts() {
  const { user } = useAuth();
  const scope = isAdmin(user) ? "all" : "mine";
  const [rules, setRules] = useState<AlertRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editRule, setEditRule] = useState<string | null>(null);
  const [expandedHistory, setExpandedHistory] = useState<string | null>(null);

  async function load() {
    try {
      const res = await alertsApi.listAlertRules(scope);
      setRules(res.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleCreate(payload: AlertPayload) {
    await alertsApi.createAlertRule(payload);
    setShowForm(false);
    load();
  }

  async function handleUpdate(id: string, payload: AlertPayload) {
    await alertsApi.updateAlertRule(id, payload);
    setEditRule(null);
    load();
  }

  async function handleDelete(id: string) {
    if (!confirm("Delete this alert rule?")) return;
    await alertsApi.deleteAlertRule(id);
    load();
  }

  async function handleToggle(rule: AlertRule) {
    await alertsApi.updateAlertRule(rule.id, {
      name: rule.name,
      description: rule.description,
      metric_sql: rule.metric_sql,
      datasource_key: rule.datasource_key,
      operator: rule.operator,
      threshold: rule.threshold,
      notify_emails: rule.notify_emails,
      notify_slack: rule.notify_slack,
      notify_slack_channels: rule.notify_slack_channels,
      is_active: !rule.is_active,
      cooldown_minutes: rule.cooldown_minutes,
    });
    load();
  }

  if (loading) return <LoadingText />;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Custom Alerts</h2>
          <p className="text-xs text-gray-500">
            Evaluated every 15 minutes. Alerts fire when the SQL result meets the condition.
            {isAdmin(user) && " Showing all users' alerts."}
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => { setShowForm(true); setEditRule(null); }}
            className="flex items-center gap-2 rounded-lg bg-[#1D345F] px-3 py-2 text-sm font-medium text-white hover:bg-[#16294d]"
          >
            <Plus size={14} />
            New rule
          </button>
        )}
      </div>

      {showForm && (
        <div className="mb-4">
          <RuleForm onSave={handleCreate} onCancel={() => setShowForm(false)} />
        </div>
      )}

      {rules.length === 0 && !showForm ? (
        <div className="rounded-xl border border-dashed border-gray-300 px-6 py-10 text-center">
          <Bell size={28} className="mx-auto mb-2 text-gray-300" />
          <p className="text-sm text-gray-500">No alert rules yet.</p>
          <p className="mt-1 text-xs text-gray-400">
            Create a rule to get notified when a metric crosses a threshold.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map((rule) => (
            <div key={rule.id} className={`rounded-xl border bg-white p-4 ${rule.is_active ? "border-gray-200" : "border-gray-100 opacity-60"}`}>
              {editRule === rule.id ? (
                <RuleForm
                  initial={{
                    name: rule.name,
                    description: rule.description ?? "",
                    metric_sql: rule.metric_sql,
                    datasource_key: rule.datasource_key ?? "",
                    operator: rule.operator,
                    threshold: rule.threshold,
                    notify_emails: rule.notify_emails ?? [],
                    notify_slack: rule.notify_slack ?? false,
                    notify_slack_channels: rule.notify_slack_channels ?? [],
                    is_active: rule.is_active ?? true,
                    cooldown_minutes: rule.cooldown_minutes ?? 60,
                  }}
                  onSave={(payload) => handleUpdate(rule.id, payload)}
                  onCancel={() => setEditRule(null)}
                />
              ) : (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-gray-900">{rule.name}</span>
                        {rule.last_fired_at && <Badge fired />}
                        {!rule.is_active && (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">paused</span>
                        )}
                      </div>
                      {rule.description && (
                        <p className="mt-0.5 text-xs text-gray-500">{rule.description}</p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
                        <span>
                          Fires when result{" "}
                          <strong className="text-gray-700">{OP_SYMBOLS[rule.operator]}</strong>{" "}
                          <strong className="text-gray-700">{Number(rule.threshold).toLocaleString()}</strong>
                        </span>
                        <span>·</span>
                        <span>Cooldown {rule.cooldown_minutes} min</span>
                        {(rule.notify_emails?.length ?? 0) > 0 && (
                          <>
                            <span>·</span>
                            <span>Email: {rule.notify_emails!.join(", ")}</span>
                          </>
                        )}
                        {rule.notify_slack && (
                          <>
                            <span>·</span>
                            <span>
                              Slack
                              {(rule.notify_slack_channels?.length ?? 0) > 0
                                ? `: ${rule.notify_slack_channels!.join(", ")}`
                                : " (default channels)"}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="mt-1.5">
                        <details>
                          <summary className="cursor-pointer text-xs text-blue-600 hover:underline">
                            View SQL
                          </summary>
                          <pre className="mt-1 overflow-x-auto rounded-lg border border-gray-200 bg-white px-3 py-2 font-mono text-xs text-gray-800 leading-relaxed">
                            {rule.metric_sql}
                          </pre>
                        </details>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        onClick={() => setExpandedHistory(expandedHistory === rule.id ? null : rule.id)}
                        title="Firing history"
                        className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      >
                        <History size={14} />
                      </button>
                      <button
                        onClick={() => handleToggle(rule)}
                        title={rule.is_active ? "Pause rule" : "Resume rule"}
                        className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      >
                        {rule.is_active ? <ToggleRight size={16} className="text-green-500" /> : <ToggleLeft size={16} />}
                      </button>
                      <button
                        onClick={() => setEditRule(rule.id)}
                        className="rounded px-2 py-1 text-xs text-blue-600 hover:bg-blue-50"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(rule.id)}
                        className="rounded p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-500"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {expandedHistory === rule.id && (
                    <FiringHistory ruleId={rule.id} onClose={() => setExpandedHistory(null)} />
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
